import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, Grid, Link, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getCachedEnvConfig, readInternalPortalUrl } from '../../../services/envConfig';
import { getWvBrandingSafe } from '../../../services/wvBrandingConfig';
import { resolvePublicUrl } from '../../../utils/publicUrl';
import { WV_STATE_SEAL_HEIGHT } from '../../../theme/wvNavFooter';
import { portalContentMaxWidthSx, portalPageHorizontalPaddingSx } from '../../../theme/portalLayout';
import { WvStackedStateWordmark } from '../WvStackedStateWordmark';
import { useNavigate } from 'react-router-dom';
import { isInternalAppPath, navigateOrAssign, scheduleScrollToElementAfterRoute } from '../../../utils/internalPath';
const Footer = ({ stateWordmark, logoText, description, linkGroups = [], phone, email, address, copyright, }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const sealSrc = getWvBrandingSafe().wvStateSealPng;
    const env = getCachedEnvConfig();
    const internalPortalHref = readInternalPortalUrl(env);
    /** Same prop as NavBar `logoText` — one stacked title beside the seal (no second line). */
    const navTitle = (typeof logoText === 'string' && logoText.trim()) ||
        (typeof stateWordmark === 'string' && stateWordmark.trim()) ||
        'West Virginia';
    /** Internal Portal link (from env) leads the second link column. */
    const footerLinkGroups = linkGroups.map((group, groupIndex) => {
        if (groupIndex !== 1 || !internalPortalHref)
            return group;
        return {
            ...group,
            links: [{ label: 'Internal Portal', href: internalPortalHref }, ...group.links],
        };
    });
    return (_jsx(Box, { component: "footer", sx: {
            bgcolor: theme.palette.primary.main,
            color: '#fff',
            pt: 6,
            pb: 3,
            mt: 'auto',
        }, children: _jsxs(Box, { sx: { ...portalContentMaxWidthSx, ...portalPageHorizontalPaddingSx }, children: [_jsxs(Grid, { container: true, spacing: 4, children: [_jsxs(Grid, { item: true, xs: 12, md: 4, children: [_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        mb: 2,
                                    }, children: [_jsx(Box, { component: "img", loading: "lazy", decoding: "async", src: resolvePublicUrl(sealSrc) ?? sealSrc, alt: "", sx: {
                                                height: WV_STATE_SEAL_HEIGHT,
                                                width: 'auto',
                                                objectFit: 'contain',
                                                flexShrink: 0,
                                                display: 'block',
                                                alignSelf: 'center',
                                            } }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }, children: _jsx(WvStackedStateWordmark, { text: navTitle, color: "#fff", size: "default", singleLine: true }) })] }), description && (_jsx(Typography, { variant: "body2", sx: {
                                        fontWeight: 400,
                                        opacity: 0.88,
                                        lineHeight: 1.65,
                                        mb: 2,
                                        maxWidth: 320,
                                    }, children: description })), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: [phone && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(PhoneIcon, { sx: { fontSize: 18, opacity: 0.7 } }), _jsx(Link, { href: `tel:${phone.replace(/\D/g, '')}`, variant: "body2", sx: { color: '#fff', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }, children: phone })] })), email && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(EmailIcon, { sx: { fontSize: 18, opacity: 0.7 } }), _jsx(Link, { href: `mailto:${email}`, variant: "body2", sx: { color: '#fff', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }, children: email })] })), address && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 1 }, children: [_jsx(LocationOnIcon, { sx: { fontSize: 18, opacity: 0.7, mt: 0.2 } }), _jsx(Typography, { variant: "body2", sx: { opacity: 0.8 }, children: address })] }))] })] }), footerLinkGroups.map((group, gi) => (_jsxs(Grid, { item: true, xs: 6, sm: 4, md: true, children: [group.title?.trim() ? (_jsx(Typography, { variant: "overline", sx: {
                                        display: 'block',
                                        color: '#fff',
                                        fontWeight: 600,
                                        mb: 2,
                                        letterSpacing: '0.08em',
                                        opacity: 0.95,
                                    }, children: group.title })) : null, _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: group.links.map((link, li) => (_jsx(Link, { 
                                        /* HashRouter: app routes like `/terms` must be `#/terms` in the DOM. */
                                        href: isInternalAppPath(link.href) ? `#${link.href}` : link.href, ...(link.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}), onClick: (e) => {
                                            if (link.openInNewTab)
                                                return;
                                            if (link.href.startsWith('#')) {
                                                e.preventDefault();
                                                const el = document.getElementById(link.href.slice(1));
                                                if (el) {
                                                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }
                                                else {
                                                    // Off the landing page (e.g. /terms) — go home, then scroll to the section.
                                                    navigate('/');
                                                    scheduleScrollToElementAfterRoute(link.href.slice(1));
                                                }
                                                return;
                                            }
                                            if (isInternalAppPath(link.href)) {
                                                e.preventDefault();
                                                navigateOrAssign(navigate, link.href);
                                            }
                                        }, variant: "body2", sx: {
                                            fontWeight: 400,
                                            color: '#fff',
                                            opacity: 0.82,
                                            textDecoration: 'none',
                                            transition: 'opacity 0.2s ease',
                                            '&:hover': { opacity: 1 },
                                        }, children: link.label }, li))) })] }, gi)))] }), _jsx(Divider, { sx: { borderColor: alpha('#fff', 0.15), my: 4 } }), _jsx(Typography, { variant: "caption", component: "div", sx: {
                        fontWeight: 400,
                        opacity: 0.78,
                        textAlign: 'center',
                        px: 1,
                    }, children: copyright || `© ${new Date().getFullYear()} West Virginia One Stop Shop. All Rights Reserved.` })] }) }));
};
export default Footer;
